import { SessionMode } from "./play-model"
import { parsedBoolOr, query } from "utils/helpers"
import {
  CLASS_ID,
  CONNECTION_ID,
  DEBUG_SUPPRESS_CONSOLE,
  DISABLE_MEMES,
  ENABLE_TIMER,
  FULL_NAME,
  GAME_CODE,
  GRAPHICAL_LOGIN,
  SHUFFLE_ANSWERS,
  SHUFFLE_SCREENS,
  USER_NAME,
  FORCE_NEW_GAME
} from "./play-constants"
import { tryParseConnectionId } from "play/connection-id"

function notUndefined(val: string | null | undefined): string | null {
  if (typeof val === "undefined") {
    val = null
  }
  return val
}

function setOrDeleteLocalStorage(key: string, val: string | null) {
  if (val) {
    console.log("setting %s=%s", key, val)
    localStorage.setItem(key, val)
  } else {
    console.log("removing %s", key)
    localStorage.removeItem(key)
  }
}

function initialSessionMode(gameCode: string | null): SessionMode | null {
  const asNum = Number(gameCode)
  if (isNaN(asNum)) return null

  if (asNum % 2 == 0) return SessionMode.Classroom
  else return SessionMode.Homework
}

class PlayEnvironment {
  private readonly _gameMode: SessionMode | null

  constructor() {
    this._gameMode = initialSessionMode(this.gameCode)
  }

  /** Determine if memes are disabled in the query string (teacher view) */
  get disableMemes() {
    return parsedBoolOr(query.getItem(DISABLE_MEMES), false)
  }

  /** Determine if timer is enabled in the query string (teacher view) */
  get enableTimer() {
    return parsedBoolOr(query.getItem(ENABLE_TIMER), false)
  }

  /** Determine if the game is for a specific class via the query string (teacher view) */
  get classId() {
    return query.getItem(CLASS_ID)
  }

  /**
   * True if students should login in their class by clicking on their name, as specified in the query string
   */
  get enableGraphicalLogin() {
    return parsedBoolOr(query.getItem(GRAPHICAL_LOGIN), false)
  }

  get shuffleAnswers() {
    return parsedBoolOr(query.getItem(SHUFFLE_ANSWERS), false)
  }

  get shuffleScreens() {
    return parsedBoolOr(query.getItem(SHUFFLE_SCREENS), false)
  }

  get userName() {
    return notUndefined(localStorage.getItem(USER_NAME))
  }

  set userName(val: string | null) {
    setOrDeleteLocalStorage(USER_NAME, val)
  }

  get fullName() {
    return notUndefined(localStorage.getItem(FULL_NAME))
  }

  set fullName(val: string | null) {
    setOrDeleteLocalStorage(FULL_NAME, val)
  }

  get connectionId() {
    let connectionId = location.hash
    if (connectionId.length > 0) {
      connectionId = location.hash.substring(1)
      const maybe = tryParseConnectionId(connectionId)
      if (maybe) {
        return connectionId
      }
    } 

    return notUndefined(localStorage.getItem(CONNECTION_ID))  
  }

  set connectionId(val: string | null) {
    setOrDeleteLocalStorage(CONNECTION_ID, val)
    location.hash = val || ''
  }

  get gameCode() {
    return query.getItem(GAME_CODE)
  }

  get oldGameCode() {
    return notUndefined(localStorage.getItem(GAME_CODE))
  }

  set oldGameCode(val: string | null) {
    setOrDeleteLocalStorage(GAME_CODE, val)
  }

  get gameMode() {
    return this._gameMode
  }

  get suppressConsole(): boolean {
    const suppressValue = query.getItem(DEBUG_SUPPRESS_CONSOLE)
    const suppress =
      suppressValue === "yes" || suppressValue === "y" || suppressValue === "1"
    return suppress
  }

  get forceNewGame() {
    return query.hasItem(FORCE_NEW_GAME)
  }
}

const playEnvironment = new PlayEnvironment()
export default playEnvironment
